import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router';
import Coachmark from 'components/Coachmark/Coachmark';
import { SEOHelmet, BreadcrumbList } from 'components/SEO';
import { useDebouncedValue } from 'hooks/useDebouncedValue';
import { useIsMobile } from 'hooks/useIsMobile';
import { useCardSizePreference } from 'hooks/useCardSizePreference';
import { CardSize } from 'components/CardSizeSelector/CardSizeSelector';
import { cardImageInputsFromCard, getCardImageUrl } from 'utils/cardImageFallback';
import { useCardsSearchInfiniteQuery } from 'store/services/cards/search/cardsSearchApi';
import {
  DEFAULT_SEARCH_STATE,
  OWNED_URL_PARAMS,
  SearchFilterState,
  deriveTokens,
  hasActiveFilters,
  isNameOnlySearch,
  parseSearchState,
  serializeSearchState,
  toSearchRequest,
} from './searchState';
import { useCollapseNavForPage } from './useCollapseNavForPage';
import { useMinimumDuration } from './useMinimumDuration';
import { useRecentSearches } from './useRecentSearches';
import ResultsToolbar from './Components/ResultsToolbar';
import ResultsGrid from './Components/ResultsGrid';
import { TRADE_GROUP_ID } from 'components/FilterPanel/FilterPanel';
import CardFilterRail from 'components/FilterPanel/CardFilterRail';
import {
  ARRIVAL_COACHMARK_BODY,
  ARRIVAL_COACHMARK_BODY_MOBILE,
  ARRIVAL_COACHMARK_BUTTON,
  ARRIVAL_COACHMARK_HEADING,
  ARRIVAL_COACHMARK_SECONDARY_MOBILE,
} from './arrivalCoachmarkCopy';
import SearchTypeahead from './Components/SearchTypeahead';
import MobileSearchDock from './Components/MobileSearchDock';
import MobileFilterSheet from './Components/MobileFilterSheet';
import 'components/ResultsPanel/ResultsPanel.css';
import './SearchWorkbenchPage.css';

const SEARCH_DEBOUNCE_MS = 250;
/** Floor on how long the loading bar stays up, so a cached hit cannot flicker it. */
const PENDING_MIN_VISIBLE_MS = 200;
const SITE = 'https://trademagic.gg';

/** Title-case a card name for the SEO title without touching the query itself. */
const titleize = (value: string) =>
  value.replace(/\b\w/g, (c) => c.toUpperCase()).trim();

const buildSeoTitle = (state: SearchFilterState): string => {
  if (state.name.trim()) {
    return `${titleize(state.name)} - MTG Cards for Trade | TradeMagic`;
  }
  return 'Search Magic Cards to Trade | Free MTG Trading Platform';
};

const buildSeoDescription = (state: SearchFilterState, total?: number): string => {
  const name = state.name.trim();
  if (name) {
    const count = total !== undefined && total > 0 ? `${total} printings of ` : '';
    return `Browse ${count}${titleize(name)} available for peer-to-peer Magic: The Gathering trading. No fees, no middleman - trade directly with collectors.`;
  }
  return 'Search 25,000+ Magic: The Gathering cards available for peer-to-peer trading. No fees, no middleman. Find the cards you need and trade directly with collectors.';
};

/**
 * The /search workbench.
 *
 * Serves both /search and /search/results: the second is a permalink of this
 * page's state, not a separate screen, so every crawled results URL keeps
 * working. Filters live in one SearchFilterState; the panel, the toolbar tokens
 * and the request are all views of it.
 */
export const SearchWorkbenchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();

  // Reclaim the nav's width for the grid, and hand it back on the way out.
  useCollapseNavForPage(!isMobile);

  /**
   * Typing cannot round-trip through the URL on every keystroke, so the page
   * keeps a draft and commits it on a debounce. `draft` drives the inputs for
   * instant feedback; `committed` drives the request and the URL.
   */
  const [draft, setDraft] = useState<SearchFilterState>(() => parseSearchState(searchParams));
  const committed = useDebouncedValue(draft, SEARCH_DEBOUNCE_MS);

  const [panelHidden, setPanelHidden] = useState(false);
  const [sheetOpen, setSheetOpen] = useState(false);

  /**
   * The offer-intent arrival coachmark.
   *
   * Read from location state ONCE, in a useState initializer, for two reasons.
   * It has to survive this page's own `setSearchParams({replace:true})` commits,
   * which fire ~250ms after mount and would otherwise re-evaluate the condition
   * mid-view. And reading it once means a later navigation that carries no
   * state cannot resurrect it.
   *
   * Nothing here is persisted, by design. This is contextual help for one
   * navigation, not an announcement: it shows on EVERY arrival from the CTA,
   * there is no hasSeen flag, and dismissal lives only in component state, so a
   * refresh that preserves the intent re-shows it. Do not add storage — the
   * gate pattern in useSmsAlertsLaunchAnnouncementGate is deliberately NOT the
   * pattern here.
   */
  const location = useLocation();
  const [arrivedWithOfferIntent] = useState(
    () => (location.state as { offerIntent?: boolean } | null)?.offerIntent === true,
  );
  const [coachmarkDismissed, setCoachmarkDismissed] = useState(false);
  const coachmarkOpen = arrivedWithOfferIntent && !coachmarkDismissed;

  /** Set only when the sheet is opened by "Show me the filter". */
  const [sheetScrollTarget, setSheetScrollTarget] = useState<string | undefined>(undefined);

  // Keep the app header out of the panel's column while the rail is showing.
  // Gated on the same condition that renders the panel, so hiding the filters
  // hands the width straight back.
  /**
   * Whether the filter rail is actually on screen, which is not the same thing
   * as the user's preference. `panelHidden` is the preference and is
   * deliberately never written on a breakpoint change, so a desktop user who
   * hid the filters, narrowed to phone width and came back still has them
   * hidden. Mobile has no rail at all, so this is false there regardless.
   *
   * The wrapper class is derived from THIS, not from `panelHidden` alone: the
   * class drives .panel-open's 330px right gutter, which exists only to clear
   * the fixed desktop rail. Without the isMobile term the gutter applied at
   * every width and stranded the results column at ~36px on a phone.
   */
  const panelVisible = !isMobile && !panelHidden;
  const [cardSize, setCardSize] = useCardSizePreference('search.cardSize', ['S', 'M', 'L']);
  const { recentSearches, addRecentSearch } = useRecentSearches();

  /**
   * The filter state this page last pushed to the URL, as a canonical string.
   * A URL whose filters differ from it came from outside (back button, an in-app
   * link) and must re-seed the draft; one that matches is our own echo.
   */
  const lastCommittedFilters = useRef<string>(serializeSearchState(draft).toString());

  // Commit the settled draft to the URL. Merged over the existing params rather
  // than replacing them, so anything this page does not own (utm_*, a partner
  // tag) survives. replace:true keeps 20 keystrokes out of the history stack.
  useEffect(() => {
    const next = serializeSearchState(committed);
    lastCommittedFilters.current = next.toString();

    setSearchParams(
      (prev) => {
        const merged = new URLSearchParams(prev);
        OWNED_URL_PARAMS.forEach((key) => merged.delete(key));
        next.forEach((value, key) => merged.set(key, value));
        return merged;
      },
      { replace: true },
    );
  }, [committed, setSearchParams]);

  // Re-seed from an external navigation. Compared on the serialized filter
  // subset, so unrelated params on the URL never look like a filter change.
  useEffect(() => {
    const incoming = serializeSearchState(parseSearchState(searchParams)).toString();
    if (incoming === lastCommittedFilters.current) return;
    lastCommittedFilters.current = incoming;
    setDraft(parseSearchState(searchParams));
  }, [searchParams]);

  const requestArgs = useMemo(() => toSearchRequest(committed), [committed]);

  const {
    data,
    isLoading,
    isFetching,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useCardsSearchInfiniteQuery(requestArgs);

  /**
   * The one loading rule for this page, driving both the toolbar and the grid.
   *
   * It is deliberately NOT the isFetching-with-previous-data idiom used elsewhere
   * (SendPage2.tsx:344, NewsfeedPage.tsx:113). serializeQueryArgs in cardsSearchApi
   * gives every filter and sort combination its own cache entry, so toggling a filter
   * does not refetch an entry — it switches to an empty one. `data` is undefined and
   * `isLoading` is true, and there is no previous data left to hold or dim.
   *
   * The third term is load-bearing. Between a keystroke or a toggle and the debounce
   * firing, no request exists yet, so every RTK flag still reads idle while the rows
   * on screen no longer answer the filters that are set. That is the window that used
   * to render "No cards match these filters".
   */
  const draftDiffersFromCommitted =
    serializeSearchState(draft).toString() !== serializeSearchState(committed).toString();
  const isResultsPending = isLoading || isFetching || draftDiffersFromCommitted;

  // A cached combination can resolve within a frame; without a floor the bar would
  // strobe rather than say anything.
  const showPending = useMinimumDuration(isResultsPending, PENDING_MIN_VISIBLE_MS);

  const cards = useMemo(() => data?.pages.flatMap((page) => page.cards) ?? [], [data]);
  // The count comes from the server's pre-pagination total, never from how many
  // rows happen to be loaded — those disagree for anything past page 1.
  const totalCount = data?.pages[0]?.totalCount;

  const tokens = useMemo(() => deriveTokens(draft), [draft]);

  /**
   * Art of the top result, shown full-bleed behind the glass.
   *
   * `cards` accumulates across pages, so `cards[0]` cannot move when page 2
   * lands — that is what keeps the backdrop still while paginating, and why
   * `isFetchingNextPage` is deliberately never read here. `isLoading` is left
   * out too: holding the outgoing art through a refetch reads calmer than
   * flashing to the floor for the debounce interval, and stale art is a
   * better failure than wrong art.
   */
  const desiredArtUrl = useMemo(() => {
    if (error) return null;
    const top = cards[0];
    return top ? getCardImageUrl(cardImageInputsFromCard(top), 'art_crop') : null;
  }, [cards, error]);

  const [artA, setArtA] = useState<string | null>(null);
  const [artB, setArtB] = useState<string | null>(null);
  const [artLayer, setArtLayer] = useState<0 | 1>(0);
  const artLayerRef = useRef<0 | 1>(0);
  /** The URL currently committed to paint — not the one we are loading. */
  const shownArtRef = useRef<string | null>(null);

  useEffect(() => {
    if (desiredArtUrl === shownArtRef.current) return;

    if (!desiredArtUrl) {
      shownArtRef.current = null;
      setArtA(null);
      setArtB(null);
      return;
    }

    let cancelled = false;
    const img = new Image();

    // Preload, then swap: only the hidden layer is written, so the swap is a
    // single opacity flip and a visible layer never changes its own image.
    const commit = () => {
      if (cancelled) return;
      shownArtRef.current = desiredArtUrl;
      const next: 0 | 1 = artLayerRef.current === 0 ? 1 : 0;
      artLayerRef.current = next;
      if (next === 0) setArtA(desiredArtUrl);
      else setArtB(desiredArtUrl);
      setArtLayer(next);
    };

    // decode() guarantees the frame is rasterized, so a progressive JPEG never
    // fades in half-drawn. Safari rejects it on some cached/CORS paths, so
    // commit on rejection too — merely less strict, never wrong.
    const settle = () => {
      if (typeof img.decode === 'function') img.decode().then(commit, commit);
      else commit();
    };

    // No crossOrigin: the pixels are never read back, and requiring CORS would
    // make the preload fail outright if the CDN omits the header.
    img.src = desiredArtUrl;
    if (img.complete) settle();
    else {
      img.onload = settle;
      // A missing art crop leaves shownArtRef unadvanced, so the previous art
      // stays put and the next query still swaps normally.
      img.onerror = () => {};
    }

    return () => {
      cancelled = true;
      img.onload = null;
      img.onerror = null;
    };
  }, [desiredArtUrl]);

  // Remember a name the user actually settled on, not every keystroke.
  useEffect(() => {
    const name = committed.name.trim();
    if (name) addRecentSearch(name);
  }, [committed.name, addRecentSearch]);

  const updateDraft = useCallback((patch: Partial<SearchFilterState>) => {
    // Touching the filter the coachmark is explaining dismisses it: the user has
    // engaged with the control, so the explanation has done its job. Scoped to
    // that one key — changing colour or CMC leaves it up, since the coachmark is
    // non-blocking and they may still be reading it.
    if ('availableToReceive' in patch) setCoachmarkDismissed(true);
    setDraft((prev) => ({ ...prev, ...patch }));
  }, []);

  const applyState = useCallback((next: SearchFilterState) => setDraft(next), []);

  // Reset means reset: both trade filters default off, so preserving them across
  // a clear would strand a filter the obvious button does not remove — the exact
  // unexplained-disappearance problem the split exists to fix.
  const clearAll = useCallback(() => setDraft(DEFAULT_SEARCH_STATE), []);

  // Turns off both trade filters and nothing else, so the empty state can undo
  // just the filter it names while every other filter the user set survives.
  const clearTradeFilters = useCallback(() => {
    setDraft((prev) => ({ ...prev, availableToSend: false, availableToReceive: false }));
  }, []);

  // SEO: a bare search and a name-only search are worth indexing; every other
  // filter permutation is a facet that would burn crawl budget.
  const nameOnly = isNameOnlySearch(committed);
  const filtered = hasActiveFilters(committed);
  const pathname = typeof window !== 'undefined' ? window.location.pathname : '/search';
  const canonical = nameOnly
    ? `${SITE}${pathname.toLowerCase().replace(/\/$/, '')}?name=${encodeURIComponent(committed.name.trim())}`
    : `${SITE}/search`;

  return (
    <>
      <SEOHelmet
        title={buildSeoTitle(committed)}
        description={buildSeoDescription(committed, totalCount)}
        canonical={canonical}
        noindex={filtered && !nameOnly}
      />
      <BreadcrumbList
        breadcrumbs={[
          { name: 'Home', url: `${SITE}/` },
          { name: 'Search Cards', url: `${SITE}/search` },
        ]}
      />

      <div className={`search-workbench ${panelVisible ? 'panel-open' : 'panel-hidden'}`}>
        {/* Sibling of the glass, never a child: backdrop-filter only blurs what
            paints behind and outside the element. */}
        <div className="search-art-bleed" aria-hidden="true">
          <div
            className="search-art-layer"
            data-active={artLayer === 0}
            style={artA ? { backgroundImage: `url("${artA}")` } : undefined}
          />
          <div
            className="search-art-layer"
            data-active={artLayer === 1}
            style={artB ? { backgroundImage: `url("${artB}")` } : undefined}
          />
          <div className="search-art-scrim" />
        </div>

        <div className="search-workbench-results results-panel">
          {/* The black chrome: search row + toolbar in one bar, capping the
              glass. Kept OUTSIDE .results-glass so the typeahead's suggestion
              list is not clipped by that element's overflow:hidden. */}
          <div className="results-chrome">
            {/* The search field is part of the header bar, not a floating
                control above it. */}
            {!isMobile && (
              <div className="results-chrome-search">
                <SearchTypeahead
                  value={draft.name}
                  onChange={(name) => updateDraft({ name })}
                  state={draft}
                  onApplyState={applyState}
                  recentSearches={recentSearches}
                />
              </div>
            )}

            <div className="results-glass-bar">
              <ResultsToolbar
                totalCount={totalCount}
                isResultsPending={showPending}
                tokens={tokens}
                onClearToken={(token) => setDraft(token.clear(draft))}
                onClearAll={clearAll}
                sort={draft.sort}
                direction={draft.direction}
                onSortChange={(sort, direction) => updateDraft({ sort, direction })}
                lang={draft.lang}
                cardSize={cardSize}
                onCardSizeChange={(size: CardSize) => setCardSize(size)}
                panelHidden={panelHidden}
                onTogglePanel={() => setPanelHidden((h) => !h)}
                isMobile={isMobile}
              />
            </div>
          </div>

          {/* Dimming the grid is a class on the container rather than a prop
              threaded through ResultsGrid: it is presentation only, and the grid
              has no business knowing a coachmark exists. */}
          <div
            className={`results-glass${coachmarkOpen ? ' results-glass--dimmed' : ''}`}
            data-testid="results-dim-host"
          >
            <div className="results-glass-body">
              <ResultsGrid
                cards={cards}
                cardSize={cardSize}
                isResultsPending={showPending}
                isFetching={isFetching}
                error={error}
                hasNextPage={!!hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                onLoadMore={fetchNextPage}
                queryKey={JSON.stringify(requestArgs)}
                onClearAll={clearAll}
                totalCount={totalCount}
                // Read from `committed`, not `draft`: these describe the query
                // that actually returned nothing, so the explanation cannot name
                // a filter the user has already toggled off mid-debounce.
                availableToSend={committed.availableToSend}
                availableToReceive={committed.availableToReceive}
                onClearTradeFilters={clearTradeFilters}
              />
            </div>
          </div>
        </div>

        {/* The shared rail (components/FilterPanel/CardFilterRail): Search's own
            model, now consumed by every surface. It writes --rail-width, which
            the header's right edge and .search-workbench's gutter read. Hidden
            (the toolbar's Hide filters) collapses the column to 0. */}
        {!isMobile && (
          <CardFilterRail
            state={draft}
            onChange={updateDraft}
            onClearAll={clearAll}
            hidden={panelHidden}
            dimmed={coachmarkOpen}
            tooltipSuppressed={coachmarkOpen}
            tradeGroupSlot={
              coachmarkOpen ? (
                <Coachmark
                  className="filter-trade-coachmark"
                  caret="up"
                  heading={ARRIVAL_COACHMARK_HEADING}
                  body={ARRIVAL_COACHMARK_BODY}
                  buttonLabel={ARRIVAL_COACHMARK_BUTTON}
                  onDismiss={() => setCoachmarkDismissed(true)}
                />
              ) : undefined
            }
          />
        )}
      </div>

      {isMobile && (
        <>
          {/* No visible anchor on mobile: the filter lives inside a closed sheet,
              so the flyout sits above the Filters button that opens it and states
              the filtered condition in words instead of pointing at a row. The
              sheet is deliberately NOT auto-opened — arriving to an open sheet
              over the results reads as a modal the user did not ask for. */}
          {coachmarkOpen && !sheetOpen && (
            <div className="search-mobile-coachmark">
              <Coachmark
                caret="down"
                heading={ARRIVAL_COACHMARK_HEADING}
                body={ARRIVAL_COACHMARK_BODY_MOBILE}
                buttonLabel={ARRIVAL_COACHMARK_BUTTON}
                secondaryLabel={ARRIVAL_COACHMARK_SECONDARY_MOBILE}
                onSecondary={() => {
                  setCoachmarkDismissed(true);
                  setSheetScrollTarget(TRADE_GROUP_ID);
                  setSheetOpen(true);
                }}
                onDismiss={() => setCoachmarkDismissed(true)}
              />
            </div>
          )}
          <MobileSearchDock
            value={draft.name}
            onChange={(name) => updateDraft({ name })}
            activeFilterCount={tokens.length}
            onOpenFilters={() => setSheetOpen(true)}
          />
          {sheetOpen && (
            <MobileFilterSheet
              state={draft}
              onChange={updateDraft}
              onClearAll={clearAll}
              onClose={() => {
                setSheetOpen(false);
                setSheetScrollTarget(undefined);
              }}
              totalCount={totalCount}
              scrollToGroupId={sheetScrollTarget}
            />
          )}
        </>
      )}
    </>
  );
};

export default SearchWorkbenchPage;
